// @owners { team: patients }
import { useState } from 'react';

/**
 * The `usePrevious` hook is a useful tool for tracking the previous value of a variable in a functional component.
 * This can be particularly handy in scenarios where it is necessary to compare the current value with the previous one, such as triggering actions or rendering based on changes.
 * @see https://react.dev/reference/react/useState#storing-information-from-previous-renders
 * @param {any} value - The value to track
 * @return {any} The previous value
 */
export const usePrevious = <T>(value: T) => {
  // Track the previous value by adjusting state during render (React's recommended pattern) rather
  // than writing/reading a ref, which the React Compiler disallows during render.
  const [current, setCurrent] = useState(value);
  const [previous, setPrevious] = useState<T | undefined>(undefined);

  if (value !== current) {
    setPrevious(current);
    setCurrent(value);
  }

  return previous;
};
